/* eslint-disable react-hooks/exhaustive-deps */

"use client";
import { cn } from "@/lib/utils";
import { motion, useAnimation } from "framer-motion";
import React, { useRef, useEffect, useCallback } from "react";

type InfiniteScrollProps = {
  children: React.ReactNode;
  speed?: number;
  gap?: "xs" | "sm" | "md" | "lg";
  label: string;
};

const InfiniteScroll: React.FC<InfiniteScrollProps> = ({
  children,
  speed = 20,
  gap = "sm",
  label,
}) => {
  const controls = useAnimation();
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const startAnimation = useCallback(() => {
    controls.start({
      x: ["0%", "-50%"],
      transition: {
        repeat: Infinity,
        ease: "linear",
        duration: speed,
      },
    });
  }, [controls, speed]);

  const stopAnimation = useCallback(() => {
    controls.stop();
  }, [controls]);

  const handleUserScroll = () => {
    stopAnimation();
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      startAnimation();
    }, 5000); // 5 seconds delay
  };

  useEffect(() => {
    startAnimation();

    const container = containerRef.current;
    if (!container) return;

    container.addEventListener("scroll", handleUserScroll, { passive: true });

    return () => {
      container.removeEventListener("scroll", handleUserScroll);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [startAnimation, stopAnimation]);

  return (
    <div
      // ref={containerRef}
      className="overflow-x-auto overflow-y-hidden w-full hide-scrollbar"
      tabIndex={0}
      role="region"
      aria-label={label}
    >
      <motion.div
        transition={{
          duration: speed,
          ease: "linear",
          repeat: Infinity,
        }}
        initial={{ translateX: 0 }}
        animate={{ translateX: "-50%" }}
        className={cn("flex w-max", {
          "gap-8": gap === "xs",
          "gap-24": gap === "sm",
          "gap-32": gap === "md",
          "gap-48": gap === "lg",
        })}
      >
        <div className="flex">{children}</div>
        {/* Duplicate content for the seamless marquee loop - hidden from
            assistive tech so it isn't announced twice. `inert` (not just
            aria-hidden) is required here since children can include
            focusable elements (links/images-as-links); aria-hidden alone
            hides them visually/from the a11y tree but doesn't stop tab
            focus, which axe flags as aria-hidden-focus. */}
        <div className="flex" aria-hidden="true" inert>
          {children}
        </div>
      </motion.div>
    </div>
  );
};

export default InfiniteScroll;
